import { get, map, kebabCase, throttle, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import SmoothScroll from 'smoothscroll-polyfill';

import EmailSignupModule from 'components/EmailSignupModule';
import { Small, Medium, LargeUp } from 'components/MediaQuery';
import Hero from 'components/SustainabilityPageHero';
import DesktopSecondaryNav from 'components/SustainabilitySecondaryNavDesktop';
import MobileSecondaryNav from 'components/SustainabilitySecondaryNavMobile';
import VideoModal from 'components/VideoModal';
import Theme from 'styles/theme';
import TransitionObserver from 'utils/TransitionObserver';
import { history } from 'utils/Url';

import PageSlide from './PageSlide';

class SustainabilityPage extends React.PureComponent {
  updateActiveSlide = throttle(() => {
    let activeIndex = 0;
    map(this.slideRefs, (ref, index) => {
      const $target = ref.current;
      if (!$target) return;
      const boundingRect = $target.getBoundingClientRect() || {};
      if (boundingRect.top - Theme.get('top-navigation-height') - boundingRect.height / 2 <= 0) {
        activeIndex = index + 1;
      }
    });
    if (this.state.activeSlideIndex !== activeIndex) {
      this.setActiveSlide(activeIndex);
    }
  }, 500);

  constructor(props) {
    super(props);
    this.state = { modalVideoUrl: '', isInverted: false, activeSlideIndex: 0 };
    this.slideRefs = map(props.pageSlides, () => React.createRef());
    this.transitionObserver = new TransitionObserver(this.slideRefs, this.setActiveSlide);
  }

  componentDidMount() {
    SmoothScroll.polyfill();
    window.addEventListener('scroll', this.updateActiveSlide);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.updateActiveSlide);
  }

  openVideoModal = url => {
    this.setState({ modalVideoUrl: url });
    this.props.openModal();
  };

  handleHeroSlideChange = isInverted => {
    this.setState({ isInverted });
  };

  handleNavClick = index => {
    const slideIndex = index - 1;

    if (slideIndex === -1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const $target = this.slideRefs[slideIndex].current;
    const targetRect = $target.getBoundingClientRect();
    const top = targetRect.top + window.pageYOffset - Theme.get('top-navigation-height');

    window.scrollTo({ top, behavior: 'smooth' });
  };

  replaceUrlHistory = activeIndex => {
    const sectionLabel =
      activeIndex === 0
        ? this.props.heroNavigationLabel
        : get(this.props.pageSlides, [activeIndex - 1, 'navigationLabel'], '');
    history.replace(`${window.location.pathname}#${kebabCase(sectionLabel)}`);
  };

  setActiveSlide = activeIndex => {
    this.replaceUrlHistory(activeIndex);
    this.setState({ activeSlideIndex: activeIndex });
  };

  onEmailSubmit = async (email, acceptsMarketing) => {
    const { signUpForNewsletter } = this.props;

    signUpForNewsletter({
      emailAddress: email,
      origination: 'signupModule',
      acceptsMarketing,
    });
  };

  render() {
    const {
      pageSlides,
      heroNavigationLabel,
      trackEvent,
      isModalOpen,
      closeModal,
      modalSettings,
      emailSignupModule,
      ...otherProps
    } = this.props;
    const { modalVideoUrl, isInverted } = this.state;

    const pageSlideLabels = [
      heroNavigationLabel,
      ...map(pageSlides, ({ navigationLabel }) => navigationLabel),
    ];

    return (
      <div className="SustainabilityPage">
        <VideoModal
          isOpen={isModalOpen}
          videoUrl={modalVideoUrl}
          closeModal={closeModal}
          modalSettings={modalSettings}
        />
        <Small>
          <MobileSecondaryNav
            slides={pageSlideLabels}
            activeSlideIndex={this.state.activeSlideIndex}
            onClick={this.handleNavClick}
            isInverted={isInverted}
          />
        </Small>
        <Medium>
          <MobileSecondaryNav
            slides={pageSlideLabels}
            activeSlideIndex={this.state.activeSlideIndex}
            onClick={this.handleNavClick}
            isInverted={isInverted}
          />
        </Medium>
        <LargeUp>
          <DesktopSecondaryNav
            slides={pageSlideLabels}
            activeSlideIndex={this.state.activeSlideIndex}
            onClick={this.handleNavClick}
            isInverted={isInverted}
          />
        </LargeUp>
        <Hero
          isInverted={isInverted}
          setIsInverted={this.handleHeroSlideChange}
          zIndex={get(pageSlides, 'length', 0) * 2}
          {...otherProps}
        />
        {map(pageSlides, (data, i) => (
          <PageSlide
            key={i}
            data={data}
            trackEvent={trackEvent}
            zIndex={(get(pageSlides, 'length', 0) - 1 - i) * 2}
            forwardedRef={this.slideRefs[i]}
          />
        ))}
        {!isEmpty(emailSignupModule) && (
          <EmailSignupModule onSubmit={this.onEmailSubmit} {...emailSignupModule} />
        )}
        <style jsx>
          {`
            .SustainabilityPage {
              position: relative;
              background-color: ${Theme.get('colors-white')};
            }
          `}
        </style>
      </div>
    );
  }
}

SustainabilityPage.propTypes = {
  pageSlides: PropTypes.arrayOf(PropTypes.shape({})),
  heroNavigationLabel: PropTypes.string,
  trackEvent: PropTypes.func,
  isModalOpen: PropTypes.bool,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  signUpForNewsletter: PropTypes.func,
  modalSettings: PropTypes.shape({}),
  emailSignupModule: PropTypes.shape({}),
};

SustainabilityPage.defaultProps = {
  heroNavigationLabel: '',
  pageSlides: [],
  trackEvent: () => {},
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  signUpForNewsletter: () => {},
  modalSettings: {},
  emailSignupModule: {},
};

export default SustainabilityPage;
